import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui';
import { formatMoney } from '../../lib/format';
import { links } from '../../lib/links';
import { useSession } from '../../lib/session';
import { KpiStrip, type Kpi } from './primitives';
import { LowStockWidget } from './widgets/LowStockWidget';
import { RecentMovementsWidget } from './widgets/RecentMovementsWidget';
import { lowStockQuery, onHoldQuery, openBatchesQuery, stockValuationQuery } from './queries';

const OPEN_STATUSES = new Set(['draft', 'in_progress', 'completed']);

/**
 * The read-only view: the state of stock and production, with nothing that looks
 * like it will change anything. Every row leads to its detail.
 */
export function ViewerDashboard() {
  const { shows } = useSession();

  const valuation = useQuery(stockValuationQuery);
  const lowStock = useQuery(lowStockQuery);
  const board = useQuery(openBatchesQuery);
  const hold = useQuery(onHoldQuery);

  const lowCount = lowStock.data?.items.length;
  const openBatches = (board.data?.items ?? []).filter((batch) => OPEN_STATUSES.has(batch.status)).length;
  const holdCount = hold.data?.items.length;

  const kpis: Kpi[] = [
    {
      label: 'Stock value',
      value: valuation.data ? formatMoney(valuation.data.totals.quantityValue) : '—',
      to: shows('reports') ? '/reports' : undefined,
      tooltip: 'Derived from the ledger at read time.',
    },
    { label: 'Low stock', value: lowCount === undefined ? '—' : String(lowCount), tone: lowCount ? 'warn' : 'good', hint: 'at or below reorder point' },
    { label: 'Open batches', value: board.isSuccess ? String(openBatches) : '—', to: shows('batches') ? '/batches' : undefined },
    { label: 'On hold', value: holdCount === undefined ? '—' : String(holdCount), hint: 'awaiting QC' },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title="What's in stock"
        description="On-hand, production and quality at a glance. This is a read-only view — nothing here changes stock."
      />

      <KpiStrip items={kpis} />

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LowStockWidget to={shows('inventory') ? links.inventoryBalances() : undefined} />
        <RecentMovementsWidget to={shows('inventory') ? links.inventoryLedger() : undefined} />
      </div>
    </>
  );
}
